// Admin — products and variants hidden because their shipping data cannot be
// trusted (owner, 2026-10-29). The holds are written by the backfill and by
// the import checks (market_check_product); this page lists them with the
// evidence and brings them back. A supplier answer or a warehouse measurement
// re-runs the checks on the server, so a product whose data is now good comes
// back on its own; "release" is for the cases a person decides.
// Asking HioBuy reuses the «منتجات بدون وزن» tools (marketAdminWeights.js).

import { marketRpc } from './marketDb';

export const PAGE = 50;

// Product reasons first, then variant reasons; 'review' ones never hide.
export const HIDE_REASONS = [
  'no_weight_large', 'no_weight_no_estimate', 'tiny_weight_underpriced', 'tiny_weight_no_estimate',
  'implausible_large_item', 'non_product_listing', 'liquid_bulk_unit', 'tiny_weight_variant', 'non_product_variant', 'manual',
];
export const REVIEW_REASONS = ['listing_review', 'implausible_dims'];
export const VARIANT_REASONS = ['tiny_weight_variant', 'non_product_variant', 'listing_review'];

export const REASON_LABEL = {
  ar: {
    no_weight_large: 'بلا وزن — منتج كبير',
    no_weight_no_estimate: 'بلا وزن ولا تقدير موثوق',
    tiny_weight_underpriced: 'وزن صغير غير معقول (5–10 غ)',
    tiny_weight_no_estimate: 'وزن أقل من 5 غ بلا تقدير',
    implausible_large_item: 'منتج كبير بوزن أو مقاس غير معقول',
    non_product_listing: 'كل خياراته ليست منتجًا (عربون/عينة/فرق سعر)',
    liquid_bulk_unit: 'سائل يُباع بالبرميل',
    tiny_weight_variant: 'خيار بوزن غير معقول',
    non_product_variant: 'خيار ليس منتجًا',
    listing_review: 'خيار «专拍» للمراجعة',
    implausible_dims: 'مقاس لا يناسب الوزن (للمراجعة)',
    manual: 'يدوي',
    import_checking: 'قيد الفحص',
  },
  en: {
    no_weight_large: 'No weight — large item',
    no_weight_no_estimate: 'No weight, no reliable estimate',
    tiny_weight_underpriced: 'Implausibly light (5–10 g)',
    tiny_weight_no_estimate: 'Under 5 g, no estimate',
    implausible_large_item: 'Large item, implausible weight or size',
    non_product_listing: 'Every variant is a non-product (deposit/sample/price difference)',
    liquid_bulk_unit: 'Liquid sold by the barrel',
    tiny_weight_variant: 'Variant with an implausible weight',
    non_product_variant: 'Non-product variant',
    listing_review: '“专拍” variant to review',
    implausible_dims: 'Size does not fit the weight (review)',
    manual: 'Manual',
    import_checking: 'Being checked',
  },
};

export const listHolds = ({ reason = '', kind = 'hide', category = '', q = '', offset = 0, limit = PAGE } = {}) =>
  marketRpc('admin_market_holds', {
    p_reason: reason || null, p_kind: kind, p_category: category || null,
    p_q: String(q || '').trim().slice(0, 100) || null, p_limit: limit, p_offset: offset,
  });

// The nightly report-only run (market_nightly_data_report, 00:40 UTC): what the
// checks WOULD hide or correct among visible products — nothing is changed.
export const latestDataReport = () => marketRpc('admin_market_data_report', {});

/** The report's would-hide total, or null when there is no report. */
export const wouldHideTotal = (rep) => (rep ? Object.values(rep.would_hide || {}).reduce((a, n) => a + Number(n || 0), 0) : null);

export const releaseHolds = (ids, reason, note) =>
  marketRpc('admin_market_hold_release', { p_product_ids: ids, p_reason: reason || null, p_note: String(note || '').trim() });

const kg = (v) => {
  const n = Number(v);
  if (!(n > 0)) return null;
  return n < 1 ? `${Math.round(n * 1000)} g` : `${Math.round(n * 100) / 100} kg`;
};

/** One short line of evidence for a hold: what the product says vs. what its type normally weighs. */
export function evidenceLine(ev, lang) {
  const ar = lang === 'ar';
  const e = ev || {};
  const parts = [];
  const own = kg(e.product_median_kg ?? e.unit_kg);
  if (own) parts.push(`${ar ? 'وزنه' : 'its weight'} ${own}`);
  else if (e.product_median_kg === null || ('variants_with_weight' in e && Number(e.variants_with_weight) === 0)) parts.push(ar ? 'بلا وزن' : 'no weight');
  const sib = kg(e.siblings_median);
  if (sib) parts.push(`${ar ? 'إخوته' : 'siblings'} ${sib}`);
  const type = kg(e.type_median);
  if (type) parts.push(`${ar ? 'المعتاد لنوعه' : 'typical for its type'} ${type}`);
  if (e.type_reliable === false) parts.push(ar ? 'النوع غير موثوق' : 'type not reliable');
  if (e.has_gemini_estimate === false) parts.push(ar ? 'بلا تقدير Gemini' : 'no Gemini estimate');
  if (e.label) parts.push(`«${String(e.label).slice(0, 60)}»`);
  if (e.why) parts.push(String(e.why));
  return parts.join(' · ');
}
